/**
 * Motion-mode replacement for PagesStrip: a scene lane on a time ruler with
 * playback, zoom, timeline undo and the document-size banner.
 *
 * Scene add / duplicate / delete go through editor commands that alias the
 * existing page handlers, so this panel never creates scenes by itself.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectPage, VideoClipRef } from '@canvas/shared-types';
import { MOTION_TEMPLATES } from '@/data/motionTemplates';
import { transitionDef } from '@/data/transitions';
import { requestResizeArtboard } from '@/lib/artboardSize';
import { runEditorCommand } from '@/lib/editorCommands';
import { useScenePosters } from '@/hooks/useScenePosters';
import { detectMotionBrowser } from '@/lib/video/browserSupport';
import { cueAt, formatTimecode } from '@/lib/video/sceneTimeline';
import { describeBudget, estimateProjectDocBytes } from '@/lib/video/videoDocLimits';
import { clipRefsInCanvasData, hasUnbackedClips, MIN_CLIP_MS } from '@/lib/video/videoClip';
import { captionMotionOf, type ReelCaptionMotion } from '@/lib/reel/reelCaption';
import { isClipProject } from '@/lib/video/timelineEdit';
import { useProjectStore } from '@/stores/projectStore';
import ClipTimeline from './ClipTimeline';
import { MAX_PX_PER_SEC, MIN_PX_PER_SEC, useVideoStore } from '@/stores/videoStore';
import { useTimelineAudioPreview } from '@/hooks/useTimelineAudioPreview';
import AudioTrackControls from './AudioTrackControls';
import AudioTrackLane from './AudioTrackLane';
import PlaybackControls from './PlaybackControls';
import Playhead from './Playhead';
import SceneStrip from './SceneStrip';
import TimelineRuler from './TimelineRuler';

/** Empty track after the last scene so its right edge stays draggable. */
const TRACK_TAIL_PX = 160;

interface VideoTimelinePanelProps {
  pages: ProjectPage[];
  activePageId: string;
  thumbnails?: Record<string, string>;
  onSelect: (pageId: string) => void;
  onRename: (pageId: string, name: string) => void;
  onReorder: (fromId: string, toId: string) => void;
  /**
   * `reel` is Movie Studio: time, cut or crossfade, and music.
   * Format, poster, and the timeline export button stay on the design canvas.
   */
  chrome?: 'motion' | 'reel';
  /** File pick for the selected scene. Reel chrome only. */
  onImportVideo?: (pageId: string, file: File) => void;
  /** Trim, speed, and mute for the clip on that scene. */
  onPatchClip?: (pageId: string, patch: Partial<VideoClipRef>) => void;
  onSplit?: () => void;
  onCaptionMotion?: (pageId: string, motion: ReelCaptionMotion) => void;
  onInsertImage?: (pageId: string, file: File) => void;
  importBusy?: boolean;
  importNote?: string | null;
}

export default function VideoTimelinePanel({
  pages,
  activePageId,
  thumbnails = {},
  onSelect,
  onRename,
  onReorder,
  chrome = 'motion',
  onImportVideo,
  onPatchClip,
  onSplit,
  onCaptionMotion,
  onInsertImage,
  importBusy = false,
  importNote = null,
}: VideoTimelinePanelProps) {
  const video = useVideoStore((s) => s.video);
  const cues = useVideoStore((s) => s.cues);
  const pxPerSec = useVideoStore((s) => s.pxPerSec);
  const currentTimeMs = useVideoStore((s) => s.currentTimeMs);
  const playback = useVideoStore((s) => s.playback);
  const canUndo = useVideoStore((s) => s.past.length > 0);
  const canRedo = useVideoStore((s) => s.future.length > 0);
  const notice = useVideoStore((s) => s.notice);
  const muted = useVideoStore((s) => s.muted);
  const hasAudio = useVideoStore((s) => Boolean(s.video?.audio));
  const { setPxPerSec, undo, redo, cueTo, setNotice, setMuted } = useVideoStore.getState();
  const currentProject = useProjectStore((s) => s.currentProject);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [docBytes, setDocBytes] = useState(0);
  const [audioSelected, setAudioSelected] = useState(false);
  const capability = useMemo(() => detectMotionBrowser().banner, []);
  const posters = useScenePosters(pages, video?.scenes);
  const stripThumbs = { ...thumbnails, ...posters };

  useTimelineAudioPreview();

  // Size estimate is a full JSON encode of the project: debounce it.
  useEffect(() => {
    if (!currentProject) return;
    const timer = window.setTimeout(() => {
      setDocBytes(estimateProjectDocBytes(currentProject));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [currentProject]);

  // Keep the playhead in view while playing.
  const playheadX = (currentTimeMs / 1000) * pxPerSec;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || playback !== 'playing') return;
    if (playheadX < el.scrollLeft || playheadX > el.scrollLeft + el.clientWidth - 40) {
      el.scrollLeft = Math.max(0, playheadX - 40);
    }
  }, [playheadX, playback]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice, setNotice]);

  if (!video) return null;

  const totalMs = video.totalDurationMs;
  const trackWidth = (totalMs / 1000) * pxPerSec + TRACK_TAIL_PX;
  const live = playback === 'stopped' ? null : cueAt(cues, currentTimeMs);
  const budget = describeBudget(docBytes);
  const unbackedMedia = video.audio?.source === 'idb' || hasUnbackedClips(pages);

  // Select first (page loads asynchronously), then park the playhead without
  // previewing — entering preview mid-load would snapshot the old page.
  const setPoster = () => {
    const cue = cues.find((c) => c.pageId === activePageId);
    if (!cue || currentTimeMs < cue.startMs || currentTimeMs > cue.endMs) {
      setNotice('Move the playhead into this scene, then set its poster.');
      return;
    }
    useVideoStore.getState().setSceneThumb(activePageId, currentTimeMs - cue.startMs);
  };

  const applyTemplate = (id: string) => {
    const template = MOTION_TEMPLATES.find((t) => t.id === id);
    if (!template) return;
    requestResizeArtboard(template.width, template.height);
    useVideoStore.getState().applyMotionTemplate(id);
    setNotice(`${template.label}: artboard ${template.width}×${template.height}.`);
  };

  const selectScene = (pageId: string) => {
    if (chrome === 'reel') setAudioSelected(false);
    onSelect(pageId);
    const cue = cues.find((c) => c.pageId === pageId);
    if (cue) cueTo(cue.startMs);
  };
  const reel = chrome === 'reel';

  if (isClipProject(video)) {
    return (
      <div className="vt-timeline" aria-label="Motion timeline">
        {capability && (
          <p className="vt-capability" role="status">
            {capability}
          </p>
        )}
        <div className="vt-toolbar">
          <PlaybackControls />
          <div className="vt-toolbar-group" role="group" aria-label="Zoom">
            <button
              type="button"
              className="vt-btn"
              title="Zoom out timeline"
              disabled={pxPerSec <= MIN_PX_PER_SEC}
              onClick={() => setPxPerSec(pxPerSec / 1.5)}
            >
              −
            </button>
            <button
              type="button"
              className="vt-btn"
              title="Zoom in timeline"
              disabled={pxPerSec >= MAX_PX_PER_SEC}
              onClick={() => setPxPerSec(pxPerSec * 1.5)}
            >
              +
            </button>
          </div>
          <div className="vt-toolbar-group" role="group" aria-label="Timeline history">
            <button type="button" className="vt-btn" title="Undo timeline change" disabled={!canUndo} onClick={undo}>
              ↶
            </button>
            <button type="button" className="vt-btn" title="Redo timeline change" disabled={!canRedo} onClick={redo}>
              ↷
            </button>
          </div>
          {notice ? (
            <span className="vt-notice" role="status">
              {notice}
            </span>
          ) : null}
          <span className="vt-toolbar-spacer" />
          <button
            type="button"
            className={`vt-btn ${muted ? 'active' : ''}`}
            aria-pressed={muted}
            onClick={() => setMuted(!muted)}
          >
            {muted ? 'Muted' : 'Sound on'}
          </button>
        </div>
        <ClipTimeline />
      </div>
    );
  }

  return (
    <div className="vt-timeline" aria-label="Motion timeline">
      {capability && (
        <p className="vt-capability" role="status">
          {capability}
        </p>
      )}
      <div className="vt-toolbar">
        <PlaybackControls />
        <div className="vt-toolbar-group" role="group" aria-label="Zoom">
          <button
            type="button"
            className="vt-btn"
            title="Zoom out timeline"
            disabled={pxPerSec <= MIN_PX_PER_SEC}
            onClick={() => setPxPerSec(pxPerSec / 1.5)}
          >
            −
          </button>
          <button
            type="button"
            className="vt-btn"
            title="Zoom in timeline"
            disabled={pxPerSec >= MAX_PX_PER_SEC}
            onClick={() => setPxPerSec(pxPerSec * 1.5)}
          >
            +
          </button>
        </div>
        <div className="vt-toolbar-group" role="group" aria-label="Timeline history">
          <button type="button" className="vt-btn" title="Undo timeline change" disabled={!canUndo} onClick={undo}>
            ↶
          </button>
          <button type="button" className="vt-btn" title="Redo timeline change" disabled={!canRedo} onClick={redo}>
            ↷
          </button>
        </div>
        <div className="vt-toolbar-group" role="group" aria-label="Scenes">
          <button type="button" className="vt-btn" title="Add scene" onClick={() => runEditorCommand('scene-add')}>
            + Scene
          </button>
          <button
            type="button"
            className="vt-btn"
            title="Duplicate scene"
            onClick={() => runEditorCommand('scene-duplicate')}
          >
            Dup
          </button>
          <button
            type="button"
            className="vt-btn"
            title="Delete scene"
            disabled={pages.length <= 1}
            onClick={() => runEditorCommand('scene-delete')}
          >
            Del
          </button>
          {!reel && (
            <button
              type="button"
              className="vt-btn"
              title="Use the playhead frame as this scene's thumbnail"
              onClick={setPoster}
            >
              Set poster
            </button>
          )}
        </div>
        {!reel && (
        <label className="vt-template">
          <span className="vt-sr">Format</span>
          <select
            aria-label="Start from a platform format"
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              e.target.value = '';
              if (id) applyTemplate(id);
            }}
          >
            <option value="">Format…</option>
            {MOTION_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        )}
        {notice ? (
          <span className="vt-notice" role="status">
            {notice}
          </span>
        ) : budget.level !== 'ok' ? (
          <span className={`vt-notice vt-budget-${budget.level}`} role="status">
            Project size {budget.label}
          </span>
        ) : unbackedMedia ? (
          <span
            className="vt-notice vt-media-unbacked"
            role="status"
            title="Some clips or music exist only in this browser. Sign in to back them up."
          >
            Media not backed up yet
          </span>
        ) : null}
        <span className="vt-toolbar-spacer" />
        {hasAudio && (
          <button
            type="button"
            className={`vt-btn ${muted ? 'active' : ''}`}
            title={muted ? 'Unmute music in preview' : 'Mute music in preview (export is unaffected)'}
            aria-pressed={muted}
            onClick={() => setMuted(!muted)}
          >
            {muted ? 'Muted' : 'Sound on'}
          </button>
        )}
        {!reel && (
          <button
            type="button"
            className="vt-btn vt-btn-primary"
            title="Export the timeline as a video file"
            onClick={() => runEditorCommand('video-export')}
          >
            Export video
          </button>
        )}
      </div>
      <div className="vt-scroll" ref={scrollRef}>
        <div className="vt-track" style={{ width: trackWidth }}>
          <TimelineRuler totalMs={totalMs} pxPerSec={pxPerSec} widthPx={trackWidth} />
          <SceneStrip
            pages={pages}
            scenes={video.scenes}
            cues={cues}
            pxPerSec={pxPerSec}
            activePageId={activePageId}
            livePageId={live?.pageId ?? null}
            thumbnails={stripThumbs}
            onSelect={selectScene}
            onRename={onRename}
            onReorder={onReorder}
          />
          <AudioTrackLane
            pxPerSec={pxPerSec}
            totalMs={totalMs}
            selected={audioSelected && hasAudio}
            onSelect={setAudioSelected}
          />
          <Playhead x={playheadX} />
        </div>
      </div>
      {reel && !(audioSelected && hasAudio) && (
        <ReelJoinBar
          pages={pages}
          activePageId={activePageId}
          onImportVideo={onImportVideo}
          onPatchClip={onPatchClip}
          onSplit={onSplit}
          onCaptionMotion={onCaptionMotion}
          onInsertImage={onInsertImage}
          importBusy={importBusy}
          importNote={importNote}
        />
      )}
      {audioSelected && hasAudio && <AudioTrackControls onClose={() => setAudioSelected(false)} />}
    </div>
  );
}

/** Length of the selected scene, its join, and the clip tools. */
function ReelJoinBar({
  pages,
  activePageId,
  onImportVideo,
  onPatchClip,
  onSplit,
  onCaptionMotion,
  onInsertImage,
  importBusy,
  importNote,
}: {
  pages: ProjectPage[];
  activePageId: string;
  onImportVideo?: (pageId: string, file: File) => void;
  onPatchClip?: (pageId: string, patch: Partial<VideoClipRef>) => void;
  onSplit?: () => void;
  onCaptionMotion?: (pageId: string, motion: ReelCaptionMotion) => void;
  onInsertImage?: (pageId: string, file: File) => void;
  importBusy: boolean;
  importNote: string | null;
}) {
  const video = useVideoStore((s) => s.video);
  const setTransition = useVideoStore((s) => s.setTransition);
  if (!video) return null;
  const index = pages.findIndex((page) => page.pageId === activePageId);
  const page = pages[index];
  const scene = page ? video.scenes.find((item) => item.pageId === page.pageId) : undefined;
  if (!page || !scene) return null;
  const def = transitionDef(scene.transitionIn.id === 'none' ? 'cut' : scene.transitionIn.id);
  const seconds = (scene.durationMs / 1000).toFixed(1);
  const offered = def.id === 'cut' || def.id === 'crossfade';
  const clip = clipRefsInCanvasData(page.canvasData)[0];
  const motion = captionMotionOf(page);
  const speeds = [0.5, 1, 2];
  const rateOptions = clip && !speeds.includes(clip.rate) ? [...speeds, clip.rate].sort((a, b) => a - b) : speeds;
  const pickFile = (file: File | undefined) => {
    if (file && onImportVideo) onImportVideo(page.pageId, file);
  };
  return (
    <div className="vt-reel-join" role="group" aria-label="Scene timing">
      <span>
        {page.name} · {seconds} s{index > 0 ? ` · ${def.label}` : ''}
      </span>
      {onCaptionMotion && (
        <label className="vt-clip-inline">
          Title
          <select
            aria-label="Title motion"
            value={motion}
            onChange={(e) => onCaptionMotion(page.pageId, e.target.value as ReelCaptionMotion)}
          >
            <option value="none">Still</option>
            <option value="fade">Fade</option>
            <option value="slide">Slide</option>
            <option value="rise">Rise</option>
          </select>
        </label>
      )}
      {index > 0 && (
        <>
          <button
            type="button"
            className={`vt-btn ${offered && def.id === 'cut' ? 'active' : ''}`}
            onClick={() => setTransition(page.pageId, { id: 'cut', durationMs: 0 })}
          >
            Cut
          </button>
          <button
            type="button"
            className={`vt-btn ${offered && def.id === 'crossfade' ? 'active' : ''}`}
            onClick={() => setTransition(page.pageId, { id: 'crossfade', durationMs: 500 })}
          >
            Crossfade
          </button>
        </>
      )}
      {clip && onPatchClip && (
        <>
          <label className="vt-audio-field">
            <span>Start</span>
            <input
              type="range"
              min={0}
              max={Math.max(0, clip.durationMs - MIN_CLIP_MS)}
              step={50}
              value={clip.trimStartMs}
              aria-label="Trim start"
              onChange={(e) => {
                const trimStartMs = Number(e.target.value);
                onPatchClip(page.pageId, {
                  trimStartMs,
                  trimEndMs: Math.max(clip.trimEndMs, trimStartMs + MIN_CLIP_MS),
                });
              }}
            />
            <output>{formatTimecode(clip.trimStartMs)}</output>
          </label>
          <label className="vt-audio-field">
            <span>End</span>
            <input
              type="range"
              min={MIN_CLIP_MS}
              max={clip.durationMs}
              step={50}
              value={clip.trimEndMs}
              aria-label="Trim end"
              onChange={(e) => {
                const trimEndMs = Number(e.target.value);
                onPatchClip(page.pageId, {
                  trimEndMs,
                  trimStartMs: Math.min(clip.trimStartMs, trimEndMs - MIN_CLIP_MS),
                });
              }}
            />
            <output>{formatTimecode(clip.trimEndMs)}</output>
          </label>
          <label className="vt-clip-inline">
            Speed
            <select
              aria-label="Clip speed"
              value={clip.rate}
              onChange={(e) => onPatchClip(page.pageId, { rate: Number(e.target.value) })}
            >
              {rateOptions.map((rate) => (
                <option key={rate} value={rate}>
                  {rate}×
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`vt-btn ${clip.muted ? 'active' : ''}`}
            aria-pressed={clip.muted}
            onClick={() => onPatchClip(page.pageId, { muted: !clip.muted })}
          >
            Mute
          </button>
          <label className="vt-audio-field">
            <span>Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={clip.volume}
              aria-label="Clip volume"
              disabled={clip.muted}
              onChange={(e) => onPatchClip(page.pageId, { volume: Number(e.target.value) })}
            />
            <output>{Math.round(clip.volume * 100)}%</output>
          </label>
        </>
      )}
      {onSplit && (
        <button type="button" className="vt-btn" onClick={onSplit} title="Split this scene at the playhead">
          Split
        </button>
      )}
      {onInsertImage && (
        <label className={`vt-btn ${importBusy ? 'disabled' : ''}`}>
          Image
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={importBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onInsertImage(page.pageId, file);
            }}
          />
        </label>
      )}
      {onImportVideo && (
        <label className={`vt-btn ${importBusy ? 'disabled' : ''}`}>
          {importBusy ? 'Importing…' : clip ? 'Replace' : 'Import video'}
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
            hidden
            disabled={importBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              pickFile(file);
            }}
          />
        </label>
      )}
      {clip?.source === 'idb' && <span className="vt-notice">This browser only</span>}
      {importNote && (
        <span className="vt-notice" role="status">
          {importNote}
        </span>
      )}
    </div>
  );
}
